import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, merge, startWith, Subject, take, takeUntil, throttleTime } from 'rxjs';
import { FormControl } from '@angular/forms';
import { TranslateModule, TranslateService, TranslateStore } from '@ngx-translate/core';
import { TrafficDeviationService } from '@components/traffic-deviations/services/traffic-deviation.service';
import { Call, RouteInfo } from '@components/traffic-deviations/interfaces/traffic-deviation-form';
import { TrafficDeviationSearchStore } from '../searches';
import { StopSearchResult } from '../searches/components/search-stop/interfaces';
import { TrafficDeviationTripService } from '@components/traffic-deviations/services/traffic-deviation-trip.service';
import { AffectedTrip } from '../deviation-affected-trip/deviation-affected-trip';

export interface TextareaWithChipsOptions {
  control: FormControl<string>;
  id: string;
  label?: string;
  caption?: string;
  asTextInput?: boolean;
  noResize?: boolean;
  editable?: string | boolean;
  createChipable?: boolean;
  editorMode?: boolean;
}

@Component({
    selector: 'app-textarea-with-chips',
    imports: [CommonModule, TranslateModule],
    templateUrl: './textarea-with-chips.component.html',
    styleUrls: ['./textarea-with-chips.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextareaWithChipsComponent implements OnChanges, AfterViewInit, OnDestroy, OnInit {
  @Input({ required: true }) options: TextareaWithChipsOptions = {
    label: '',
    caption: '',
    asTextInput: false,
    noResize: undefined,
    createChipable: false,
    editorMode: false
  } as TextareaWithChipsOptions;
  @Input() editable: string | boolean = 'false';
  @Input() invalid: boolean = false;
  @Output() focused = new EventEmitter<string>();

  @ViewChild('textarea') textarea: ElementRef<HTMLDivElement>;

  private readonly _translateService = inject(TranslateService);
  private readonly _translateStore = inject(TranslateStore);
  private readonly _trafficDeviationService = inject(TrafficDeviationService);
  private readonly _trafficDeviationTripService = inject(TrafficDeviationTripService);
  private readonly _trafficDeviationSearchStore = inject(TrafficDeviationSearchStore);

  public allChipsHasValue$ = new BehaviorSubject<{ [key: string]: boolean }>({});

  private _cursorPosition = null;
  private _removedText = false;
  private readonly _destroy$ = new Subject<void>();

  // Initialize noResize based on asTextInput in ngOnInit
  ngOnInit() {
    if (this.options.asTextInput) {
      this.options.noResize = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.editable) {
      if (changes.editable.currentValue !== changes.editable.previousValue) {
        this._setTextboxContenteditable(String(this.editable));
      }
    }
  }

  ngAfterViewInit(): void {
    this._setTextboxContenteditable(String(this.editable));

    this.options.control.valueChanges.pipe(
      startWith(this.options.control.value),
      takeUntil(this._destroy$)
    ).subscribe((text) => {
      let innerHTML = this.textarea.nativeElement.innerHTML;

      if (text !== innerHTML) {
        this.textarea.nativeElement.innerHTML = text;
      }

      let restoreCursorPositionAfterTriggerHashReplace = false;
      // check if user tries to add chip manually to the textarea
      if (this.textarea.nativeElement === document.activeElement) {
        const hashIndex = text.indexOf('#');
        if (hashIndex !== -1) {
          const endHashIndex = text.indexOf('#', hashIndex + 1);
          if (endHashIndex !== -1) {
            restoreCursorPositionAfterTriggerHashReplace = true;
            const hashText = text.slice(hashIndex + 1, endHashIndex - 1);
            this._cursorPosition += hashText.length;
          }
        }
      }

      this.allChipsHasValue$.next({});
      this._triggerHashReplace();
      const textHasSpanVariable = text.indexOf('<span class="chip"') !== -1;
      if (text !== this.textarea.nativeElement.innerHTML || textHasSpanVariable) {
        this._removeAllHashVariables();
        !this.options.editorMode && this._mapChipsToData();
      } else {
        if (text === '') {
          const form = this._trafficDeviationService.trafficDeviationForm;
          const allChipsHasValue = form?.controls.trafficDeviationInformation.controls.allChipsHasValue;
          allChipsHasValue?.setValue(true);
        }

        this.options.control.setValue(this.textarea.nativeElement.innerHTML, { emitEvent: false });
      }

      // when the user adds a chip by printing out "#variable#" in the textarea
      if (restoreCursorPositionAfterTriggerHashReplace) {
        this._restoreCursorPosition();
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /** Clears all html and makes the chip into text only. */
  public getTextWithoutChips(): string {
    return this._clearHTML(this.textarea.nativeElement.innerHTML);
  }

  /**
   * Creates a span element and places it at the cursor position inside the textarea
   * @param text the text for inside the chip
   */
  public insertElementAtCursorPosition(text: string): void {
    let range = this._restoreCursorPosition();
    const node = this._getChipSpanNode(text);
    range.insertNode(node);

    this._cursorPosition += node.textContent.length;

    let totalTextLength = 0;
    Array.from(range.endContainer.childNodes)?.forEach((x) => {
      totalTextLength += x.textContent.length;
    });

    if (this._cursorPosition >= totalTextLength) {
      // if the position is at the end
      range = this._restoreCursorPosition();
      range.insertNode(document.createTextNode(' '));
    }

    range = this._createRange(this.textarea.nativeElement, this._cursorPosition);
    range.setEnd(range.startContainer, 0);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    this.options.control.setValue(this.textarea.nativeElement.innerHTML);
  }

  /** Gets all the chips in the current template */
  public getChipsToMapData(): HTMLSpanElement[] {
    return Array.from(this.textarea.nativeElement.querySelectorAll('.chip'));
  }

  public getUsedVariables(): Set<string> {
    const usedVariables: Set<string> = new Set();
    const spanElements = this.getChipsToMapData();

    if (!spanElements?.length) return new Set<string>();

    const translations = this._translateStore.getTranslations(this._translateService.currentLang) as Record<string, unknown>;
    const translationValues = Object.values(translations);
    const translationKeys = Object.keys(translations);

    spanElements.forEach((elem) => {
      const translationIndex = translationValues.indexOf(elem.getAttribute('value'));
      if (translationIndex !== -1) {
        const key = translationKeys[translationIndex];
        usedVariables.add(key);
      } else {
        usedVariables.add(elem.getAttribute('value'));
      }
    });

    return usedVariables;
  }

  /** Label click sets focus on "textarea", need this because the div is not labelable */
  protected onClickSetFocus(): void {
    this.textarea.nativeElement.focus();
  }

  /**
   * Deactivate new line for when "asTextInput" is true.
   * @param event KeyboardEvent
   */
  protected onKeypressEvent(event: KeyboardEvent): void {
    if (this.options.asTextInput && event.key === 'Enter') {
      event.preventDefault();
      return;
    }

    if (
      event.key === 'ArrowRight'
      || event.key === 'ArrowLeft'
      || event.key === 'ArrowUp'
      || event.key === 'ArrowDown'
    ) {
      setTimeout(() => {
        this._storeCursorPosition();
      }, 0);
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      this._removedText = true;
    }
  }

  /** On input for catching each event on user input, makes sure the control is updated. */
  protected onInput(): void {
    this._storeCursorPosition();

    const rawHTML = this.textarea.nativeElement.innerHTML;
    // Normalize: if the visible text content is empty (browser may insert <br> in single-line mode), treat as ''
    const text = this.textarea.nativeElement.textContent === '' ? '' : rawHTML;
    if (text !== rawHTML) {
      this.textarea.nativeElement.innerHTML = text;
    }
    this.options.control.setValue(text);

    if (!this.options.control.dirty) {
      this.options.control.markAsDirty();
      this.options.control.updateValueAndValidity();
    };
  }

  /** Make sure to save the cursor position */
  protected onClick(): void {
    this._storeCursorPosition();
  }

  /**
   * Blur event to make the textfield act like a FormControl since it's an extension of a control
   */
  protected onBlur(): void {
    if (!this.options.control.touched) {
      this.options.control.markAsTouched();
      this.options.control.updateValueAndValidity();
    };
  }

  /** Clears all html and makes the chip into text only. */
  private _clearHTML(html: string): string {
    return html.replace(/<[^>]*>/g, '').replace(/\&nbsp;/g, ' ').trimEnd();
  }

  /**
   * First iterate all textArea components and finds all chips,
   * secondly take the value attribute from each chip which is the controller name to bind against.
   * If controller exists we subscribe to valueChange, take value and then replaces the chip text.
   */
  private _mapChipsToData(): void {
    const form = this._trafficDeviationService.trafficDeviationForm;
    const affected = this._trafficDeviationService.trafficDeviationForm.controls.affected;
    const staticCustom = form.controls.trafficDeviationInformation.controls.custom.controls[0];
    const custom = form.controls.trafficDeviationInformation.controls.custom.controls[1];

    this.allChipsHasValue$.next({});
    let controlsInRouteInfo: string[];
    this._trafficDeviationService.automaticRouteInfoVariable$.pipe(
      take(1)).subscribe((variables) => {
        controlsInRouteInfo = [...variables];
      });
    const chips = this.getChipsToMapData();

    for (const chip of chips) {
      const chipValue = chip.getAttribute('value');
      if (controlsInRouteInfo.includes(chipValue)) {
        this.allChipsHasValue$.next({ ...this.allChipsHasValue$.getValue(), [chipValue]: false });
        continue;
      }

      let customControl: FormControl<string>;
      switch (chipValue) {
        case 'Start date and Time': {
          this.allChipsHasValue$.next({ ...this.allChipsHasValue$.getValue(), [chipValue]: false });
          const startDate = affected.controls.startDate;
          const startTime = affected.controls.startTime;

          combineLatest([
            startDate.valueChanges.pipe(startWith(startDate.value)),
            startTime.valueChanges.pipe(startWith(startTime.value))
          ]).pipe(
            takeUntil(merge(this._destroy$, this.options.control.valueChanges))
          ).subscribe(([startDate, startTime]) => {
            const controlChip = this.textarea.nativeElement?.querySelector(
              `.chip[value=${CSS.escape(chipValue)}]`
            );

            if (startDate && startTime) {
              startTime = startTime.length > 5 ? startTime.slice(0, -3) : startTime;
              controlChip.innerHTML = `${startDate} kl ${startTime}`;
            } else controlChip.innerHTML = this._translateService.instant(chipValue);

            this.allChipsHasValue$.next({
              ...this.allChipsHasValue$.getValue(),
              [chipValue]: controlChip.innerHTML !== this._translateService.instant(chipValue)
            });
          });
          break;
        }
        case 'End date and Time': {
          this.allChipsHasValue$.next({ ...this.allChipsHasValue$.getValue(), [chipValue]: false });
          const endDate = affected.controls.endDate;
          const endTime = affected.controls.endTime;

          combineLatest([
            endDate.valueChanges.pipe(startWith(endDate.value)),
            endTime.valueChanges.pipe(startWith(endTime.value))
          ]).pipe(
            takeUntil(merge(this._destroy$, this.options.control.valueChanges))
          ).subscribe(([endDate, endTime]) => {
            const controlChip = this.textarea.nativeElement?.querySelector(
              `.chip[value=${CSS.escape(chipValue)}]`
            );

            if (endDate && endTime) {
              endTime = endTime.length > 5 ? endTime.slice(0, -3) : endTime;
              controlChip.innerHTML = `${endDate} kl ${endTime}`;
            } else controlChip.innerHTML = this._translateService.instant(chipValue);

            this.allChipsHasValue$.next({
              ...this.allChipsHasValue$.getValue(),
              [chipValue]: controlChip.innerHTML !== this._translateService.instant(chipValue)
            });
          });
          break;
        }
        case 'Cause':
          this._defaultChipSubscription(form.controls.deviationReason, chipValue, true);
          break;
        case 'Start date':
          this._defaultChipSubscription(affected.controls.startDate, chipValue);
          break;
        case 'End date':
          this._defaultChipSubscription(affected.controls.endDate, chipValue);
          break;
        case 'Minutes':
          this._defaultChipSubscription(affected.controls.minutes, chipValue);
          break;
        case 'Time':
        case 'Departures':
        case 'Date and Time':
        case 'Amount of Minutes':
        case 'CustomFieldDescription':
          this.allChipsHasValue$.next({ ...this.allChipsHasValue$.getValue(), [chipValue]: false });
          customControl = staticCustom?.get(chipValue) as FormControl<string>;
          break;
        default:
          if (!controlsInRouteInfo.includes(chipValue)) {
            this.allChipsHasValue$.next({ ...this.allChipsHasValue$.getValue(), [chipValue]: false });
            customControl = custom?.get(chipValue) as FormControl<string>;
          }
          break;
      }

      if (customControl) {
        customControl.valueChanges.pipe(
          startWith(customControl.value),
          takeUntil(merge(this._destroy$, this.options.control.valueChanges))
        ).subscribe((customValue) => {
          const controlChip: HTMLElement = this.textarea.nativeElement?.querySelector(
            `.chip[value=${CSS.escape(chipValue)}]`
          );
          if (chipValue === 'Date and Time') customValue = customValue.replace('T', ' kl ');
          if (controlChip) {
            controlChip.textContent = customValue || this._translateService.instant(chipValue);
          }

          this.allChipsHasValue$.next({
            ...this.allChipsHasValue$.getValue(),
            [chipValue]: controlChip.textContent !== this._translateService.instant(chipValue)
          });
        });
      }
    }

    this._subscribeToRouteInfoAndBindToChip(chips, controlsInRouteInfo);
  }

  private _defaultChipSubscription(control: FormControl, chipValue: string, lowercase: boolean = false): void {
    this.allChipsHasValue$.next({ ...this.allChipsHasValue$.getValue(), [chipValue]: false });
    combineLatest([
      control.valueChanges.pipe(startWith(control.value)),
      this._trafficDeviationService.swedishTranslations$
    ]).pipe(
      takeUntil(merge(this._destroy$, this.options.control.valueChanges))
    ).subscribe(([value, swedishTranslations]) => {
      const controlChip = this.textarea.nativeElement?.querySelector(
        `.chip[value=${CSS.escape(chipValue)}]`
      );

      if (!controlChip) return;

      if (value) {
        let translated = (swedishTranslations as {[key: string]: string})[value] ?? value;

        if (lowercase) {
          translated = translated.toLowerCase();
        }

        controlChip.innerHTML = translated;
      } else {
        controlChip.innerHTML = this._translateService.instant(chipValue);
      }

      this.allChipsHasValue$.next({
        ...this.allChipsHasValue$.getValue(),
        [chipValue]: controlChip.innerHTML !== this._translateService.instant(chipValue)
      });
    });
  }

  /**
   * When mapping chips to form data, this is where all routeInfo chips are handled
   * @param chips 
   * @param controlsInRouteInfo 
   */
  private _subscribeToRouteInfoAndBindToChip(
    chips: Element[], controlsInRouteInfo: string[]
  ): void {
    const form = this._trafficDeviationService.trafficDeviationForm;
    const routeInfo = form.controls.affected.controls.routeInfo;
    routeInfo.valueChanges.pipe(
      startWith(routeInfo.value),
      throttleTime(10, null, { leading: false, trailing: true }),
      takeUntil(merge(this._destroy$, this.options.control.valueChanges))
    ).subscribe((routeInfo: RouteInfo[]) => {
      const calls: Call[] = [];
      for (const route of routeInfo) {
        for (const call of route.calls) {
          if (call.placeId) calls.push(call);
        }
      }

      for (const chip of chips) {
        const chipValue = chip.getAttribute('value');
        const controlChips: NodeListOf<HTMLElement> = this.textarea.nativeElement?.querySelectorAll(
          `.chip[value=${CSS.escape(chipValue)}]`
        );

        if (!controlChips) continue;
        if (controlsInRouteInfo.includes(chipValue)) {
          const stops = this._trafficDeviationSearchStore.stateSnapshot.selectedStopSearches;
          let affectedTrip: AffectedTrip = null;
          this._trafficDeviationTripService.selectedTrip$.pipe(take(1)).subscribe(trip => {
            affectedTrip = trip;
          });

          controlChips.forEach((controlChip: HTMLElement) => {
            this._handleRouteInfoChips(
              routeInfo,
              calls,
              stops,
              affectedTrip,
              chipValue,
              controlChip
            );

            if (!controlChip.innerHTML) {
              controlChip.innerHTML = this._translateService.instant(chipValue);
            }

            this.allChipsHasValue$.next({
              ...this.allChipsHasValue$.getValue(),
              [chipValue]: controlChip.innerHTML !== this._translateService.instant(chipValue)
            });
          });
        }
      }
    });
  }

  /**
   * Specific logic inside routeInfo subscription for all the routeInfo chips
   * @param routeInfo 
   * @param calls 
   * @param stops 
   * @param affectedTrip 
   * @param chipValue 
   * @param controlChip 
   */
  private async _handleRouteInfoChips(
    routeInfo: RouteInfo[], calls: Call[], stops: StopSearchResult[], affectedTrip: AffectedTrip, chipValue: string, controlChip: HTMLElement
  ): Promise<void> {
    switch (chipValue) {
      case 'Stops': {
        const stopNames: string[] = [...stops.filter(stop => stop.type == "StopArea").map((stop) => stop.description),
        ...calls.map((call) => call.name)];
        controlChip.innerHTML = this._formatStringArrayForChip(stopNames);
        break;
      }
      case 'Routenumber':
        if (routeInfo.length) {
          const routeNames = routeInfo.map((r) => r.routePublicName);
          controlChip.innerHTML = this._formatStringArrayForChip(routeNames);
        } else controlChip.innerHTML = '';
        break;
      case 'Stop name':
        controlChip.innerHTML = routeInfo[0]?.calls[0]?.name ?? '';
        break;
      case 'Retracted Stops': {
        const retractedCallNames: string[] = calls.map((call) => call.name);
        controlChip.innerHTML = this._formatStringArrayForChip(retractedCallNames);
        break;
      }
      case 'frånHållplats':
        controlChip.innerHTML = calls[0]?.name.slice(0, -2) ?? '';
        break;
      case 'tillHållplats':
        controlChip.innerHTML = calls[calls.length - 1]?.name.slice(0, -2) ?? '';
        break;
      case 'Routenumber to Direction': {
        const linjer: string[] = routeInfo.map((linje) => {
          if (linje?.routePublicName && linje?.firstStopDestinationSign) {
            return `${linje.routePublicName} mot ${linje.firstStopDestinationSign}`;
          }
        });
        if (linjer?.length && linjer[0]) {
          controlChip.innerHTML = this._formatStringArrayForChip(linjer);
        } else {
          controlChip.innerHTML = '';
        }
        break;
      }
      case 'Trips Start Stop': {
        if (affectedTrip) {
          controlChip.innerHTML = affectedTrip.startPlace ?? '';
        }
        break;
      }
      case 'Trips Start Time': {
        if (affectedTrip) {
          controlChip.innerHTML = affectedTrip.startTime ?? '';
        }
        break;
      }
      case 'Trips End Stop': {
        if (affectedTrip) {
          controlChip.innerHTML = affectedTrip.endPlace ?? '';
        };
        break;
      }
      case 'First retracted to last retracted stop': {
        const callNames: string[] = calls.map((call) => call.name);
        if (callNames.length > 1) {
          controlChip.innerHTML = `${callNames[0]} till ${callNames[callNames.length - 1]}`;
        } else if (callNames.length === 1) {
          controlChip.innerHTML = `${callNames[0]}`;
        } else controlChip.innerHTML = '';
        break;
      }
      case 'New stops': {
        const callNames: string[] = calls.filter((c) => c.replacePlaceId !== 'retracted').map((call) => call.replaceName);
        controlChip.innerHTML = this._formatStringArrayForChip(callNames);
        break;
      }
    }
  }

  /**
   * Returns string array in preferred format with unique values only
   */
  private _formatStringArrayForChip(array: string[]): string {
    if (!array.length) return '';

    array = [...new Set(array)];

    const lastElement = array.pop();
    let text = array.join(', ');
    if (!text) return lastElement;

    text += ` och ${lastElement}`;
    return text;
  }

  /**
   * Sets the Contenteditable attribute on the textarea.
   * @param flag boolean for contenteditable
   */
  private _setTextboxContenteditable(value: string): void {
    this.textarea?.nativeElement.setAttribute(
      'contenteditable', value
    );
  }

  /**
   * Recursively calls _replaceHashesWithChips() until no "#" exists.
   */
  private _triggerHashReplace(): void {
    let innerHTML = this.textarea.nativeElement.innerHTML;
    if (!innerHTML) return;
    const hasHash = this._hasHash(innerHTML);
    if (hasHash) {
      this._replaceHashesWithChips();
      this._triggerHashReplace();
    }
  }

  /**
   * Finds the "#" to replace with a Chip
   */
  private _replaceHashesWithChips(): string {
    const innerHTML = this.textarea.nativeElement.innerHTML;
    const hashIndex = innerHTML.indexOf('#');
    const endOfHashIndex = this._endOfHashStringIndex(innerHTML, hashIndex);
    let hashedText: string;

    if (endOfHashIndex !== -1) {
      hashedText = innerHTML.substring(hashIndex, endOfHashIndex);
    } else return;

    const textWithoutHash = hashedText.replace('#', '').replace('#', '');
    const chipSpanHTML = this._getChipSpanNode(textWithoutHash);

    const position = this.textarea.nativeElement.textContent.indexOf('#');

    let range = this._createRange(this.textarea.nativeElement, position);
    range.insertNode(chipSpanHTML);

    this.textarea.nativeElement.innerHTML = this.textarea.nativeElement.innerHTML.replace(hashedText, '');
  }

  /**
   * Does innerHTML include an '#'
   * @param innerHTML 
   * @returns boolean
   */
  private _hasHash(innerHTML: string): boolean {
    const hashIndex = innerHTML.indexOf('#');
    if (hashIndex !== -1) {
      const endHashIndex = innerHTML.indexOf('#', hashIndex + 1);
      return endHashIndex !== -1;
    };
    return false;
  }

  /**
   * Finds the index for the end of the hash string.
   * @param innerHTML string
   * @param hashIndex number
   * @param usesQuotationMark boolean for adding logic to handle quotationmarks
   * @returns index for the end of string belonging to the #
   */
  private _endOfHashStringIndex(
    innerHTML: string,
    hashIndex: number
  ): number {
    return innerHTML.indexOf('#', hashIndex + 1) + 1;
  }

  /**
   * Removes all hash variables in the textarea after the _triggerHashReplace() have run and the 
   * textarea content are changed
   */
  private _removeAllHashVariables(): void {
    if (!this._hasHash(this.textarea.nativeElement.innerHTML)) return;

    const removeHashVariable = () => {
      const text = this.textarea.nativeElement.innerHTML;
      if (this._hasHash(text)) {
        const hashIndex = text.indexOf('#');
        const endOfHashIndex = this._endOfHashStringIndex(text, hashIndex);
        const hashedText = text.substring(hashIndex, endOfHashIndex);
        this.textarea.nativeElement.innerHTML = text.replace(hashedText, '');
        removeHashVariable();
      }
    }
    removeHashVariable();
  }

  /**
   * Gets the HTML string for the Chip.
   * @param text string
   * @returns HTML string
   */
  private _getChipSpanNode(text: string): HTMLSpanElement {
    const node = document.createElement('span');
    node.classList.add('chip');
    node.contentEditable = 'false';
    const translations = this._translateStore.getTranslations(this._translateService.currentLang) as Record<string, unknown>;

    let valueKey = text;
    const translationIndex = Object.values(translations).indexOf(text);
    if (translationIndex !== -1) {
      const key = Object.keys(translations)[translationIndex];
      valueKey = key;
    } else {
      const key = Object.keys(translations).indexOf(text);
      if (key !== -1) {
        text = Object.values(translations)[key] as string;
      }
    }

    node.setAttribute('value', valueKey);
    node.textContent = text;
    return node;
  }

  private _storeCursorPosition() {
    if (!window.getSelection) return;

    const selection = window.getSelection();
    if (selection.rangeCount <= 0) return;

    let range = selection.getRangeAt(0);
    const clone = range.cloneRange();

    clone.selectNodeContents(this.textarea.nativeElement);
    clone.setEnd(range.endContainer, range.endOffset);

    this._cursorPosition = clone.toString().length;
    this.focused.emit(this.options.id);

    let textLength = 0;
    let totalTextLength = 0;
    Array.from(clone.startContainer.childNodes)?.forEach((x) => {
      totalTextLength += x.textContent.length;
    });

    for (const node of Array.from(clone.startContainer.childNodes)) {
      textLength += node.textContent.length;
      if (textLength < this._cursorPosition) continue;
      if ((<Element>node).tagName === 'SPAN') {
        if (!this._removedText && totalTextLength === this._cursorPosition) {
          // if the cursor position is at the end and inside SPAN node create a textnode with 
          // empty space to make sure the cursor are visible, since cursors are not visible if 
          // the last element is an span which is not contenteditable

          range = this._restoreCursorPosition();
          range.insertNode(document.createTextNode(' '));

          range = this._createRange(this.textarea.nativeElement, this._cursorPosition);
          range.setEnd(range.startContainer, 0);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          break;
        }

        this._removedText = false;
      }
      break;
    }
  }

  private _createRange(node: Node, targetPosition: number): Range {
    let range = document.createRange();
    range.selectNode(node);

    let pos = 0;
    const stack = [node];
    while (stack.length > 0) {
      const current = stack.pop();

      if (current.nodeType === Node.TEXT_NODE) {
        const len = current.textContent.length;
        if (
          pos + len >= targetPosition
          && (<HTMLElement>current.parentNode).classList.contains('chip-text-area')
        ) {
          const offset = targetPosition - pos < 0 ? 0 : targetPosition - pos;
          range.setStart(current, offset);
          return range;
        }
        pos += len;
      } else if (current.childNodes && current.childNodes.length > 0) {
        for (let i = current.childNodes.length - 1; i >= 0; i--) {
          stack.push(current.childNodes[i]);
        }
      }
    }

    // The target position is greater than the
    // length of the contenteditable element.
    range.setStart(node, node.childNodes.length);
    return range;
  }

  private _restoreCursorPosition(): Range {
    const range = this._createRange(this.textarea.nativeElement, this._cursorPosition);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    this.textarea.nativeElement?.focus();

    return range;
  }
}